import { Helmet } from "react-helmet-async";
import { Layout } from "@/components/layout/Layout";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { 
  ArrowUpFromLine, 
  CheckCircle2, 
  Clock,
  Phone,
  ArrowLeft,
  ArrowRight,
  Users,
  Award,
  AlertTriangle,
  Shield,
  BookOpen,
  FileCheck
} from "lucide-react";
import workAtHeightHero from "@/assets/training/work-at-height-hero.jpg";

export default function WorkHeight() {
  const { t, i18n } = useTranslation('training-pages');
  const isRTL = i18n.language === 'he';
  const ArrowIcon = isRTL ? ArrowLeft : ArrowRight;

  const courseDetails = t('workHeight.courseDetails', { returnObjects: true }) as {
    duration: string;
    certification: string;
    participants: string;
    location: string;
  };

  const syllabusTheoretical = t('workHeight.syllabus.theoretical', { returnObjects: true }) as {
    title: string;
    duration: string;
    topics: string[];
  };

  const syllabusPractical = t('workHeight.syllabus.practical', { returnObjects: true }) as {
    title: string;
    duration: string;
    topics: string[];
  };

  const syllabus = [syllabusTheoretical, syllabusPractical];

  const targetAudience = t('workHeight.targetAudience.items', { returnObjects: true }) as string[];

  const legalRequirements = t('workHeight.legalRequirements', { returnObjects: true }) as Array<{
    title: string;
    description: string;
  }>;

  const services = t('workHeight.services.items', { returnObjects: true, defaultValue: [] }) as Array<{
    title: string;
    description: string;
  }> || [];

  const faqs = t('workHeight.faqs', { returnObjects: true, defaultValue: [] }) as Array<{
    question: string;
    answer: string;
  }> || [];

  return (
    <Layout>
      <Helmet>
        <title>{t('workHeight.meta.title')}</title>
        <meta name="description" content={t('workHeight.meta.description')} />
        <link rel="canonical" href="https://adam-safety.com/training/work-height" />
        <meta property="og:title" content={t('workHeight.meta.title')} />
        <meta property="og:description" content={t('workHeight.meta.description')} />
        <meta property="og:url" content="https://adam-safety.com/training/work-height" />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={t('workHeight.meta.title')} />
        <meta name="twitter:description" content={t('workHeight.meta.description')} />
      </Helmet>

      {/* Hero Section */}
      <section className="relative min-h-[70vh] flex items-center justify-center overflow-hidden">
        <div 
          className="absolute inset-0 z-0"
          style={{ background: 'var(--gradient-hero)' }}
        >
          <div className="absolute inset-0 pattern-hero opacity-30" />
        </div>

        <div className="container relative z-10 px-4 py-20 md:py-32">
          <div className="grid md:grid-cols-2 gap-12 items-center max-w-6xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className={isRTL ? 'md:order-1' : ''}
            >
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-accent/20 text-accent mb-6">
                <ArrowUpFromLine className="w-5 h-5" />
                <span className="text-sm font-medium">{t('common.safetyTraining')}</span>
              </div>
              
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-primary-foreground mb-6 leading-tight">
                {t('workHeight.hero.title')}
              </h1>
              
              <p className="text-lg md:text-xl text-primary-foreground/80 mb-8 leading-relaxed">
                {t('workHeight.hero.subtitle')}
              </p>

              <div className="flex flex-wrap gap-3 mb-8">
                <div className="flex items-center gap-2 bg-primary-foreground/10 px-4 py-2 rounded-full">
                  <Clock className="w-4 h-4 text-accent" />
                  <span className="text-primary-foreground text-sm">{courseDetails.duration}</span>
                </div>
                <div className="flex items-center gap-2 bg-primary-foreground/10 px-4 py-2 rounded-full">
                  <Award className="w-4 h-4 text-accent" />
                  <span className="text-primary-foreground text-sm">{courseDetails.certification}</span>
                </div>
                <div className="flex items-center gap-2 bg-primary-foreground/10 px-4 py-2 rounded-full">
                  <Users className="w-4 h-4 text-accent" />
                  <span className="text-primary-foreground text-sm">{courseDetails.participants}</span>
                </div>
              </div>

              <Link to="/contact">
                <Button variant="cta" size="lg" className="gap-2 text-lg px-8">
                  <Phone className="w-5 h-5" />
                  {t('common.orderTraining')}
                </Button>
              </Link>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className={isRTL ? 'md:order-0' : ''}
            >
              <img 
                src={workAtHeightHero} 
                alt={t('workHeight.hero.title')}
                className="rounded-2xl shadow-2xl w-full object-cover max-h-[500px]"
                loading="lazy"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      {Array.isArray(services) && services.length > 0 && (
        <section className="section-padding bg-background">
          <div className="container px-4">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
                {t('workHeight.services.title')}
              </h2>
            </motion.div>

            <div className="grid md:grid-cols-2 gap-6 max-w-5xl mx-auto">
              {services.map((service, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="card-service flex items-start gap-4 p-6"
                >
                  <CheckCircle2 className="w-6 h-6 text-accent mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="text-lg font-bold text-foreground mb-2">{service.title}</h3>
                    <p className="text-muted-foreground">{service.description}</p>
                  </div>
                </motion.div>
              ))}
            </div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mt-10"
            >
              <Link to="/contact">
                <Button variant="cta" size="lg" className="gap-2 text-lg px-8">
                  <Phone className="w-5 h-5" />
                  {t('common.orderTraining')}
                </Button>
              </Link>
            </motion.div>
          </div>
        </section>
      )}

      {/* Course Details / Syllabus */}
      <section className="section-padding section-alt">
        <div className="container px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              {t('workHeight.syllabus.title')}
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              {t('workHeight.syllabus.subtitle')}
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {syllabus.map((section, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="card-service"
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className="icon-container">
                    {index === 0 ? <BookOpen className="w-6 h-6" /> : <Shield className="w-6 h-6" />}
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-foreground">{section.title}</h3>
                    <span className="text-sm text-accent">{section.duration}</span>
                  </div>
                </div>
                <ul className="space-y-3">
                  {section.topics.map((topic, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <CheckCircle2 className="w-5 h-5 text-accent mt-0.5 flex-shrink-0" />
                      <span className="text-muted-foreground">{topic}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Target Audience */}
      <section className="section-padding bg-background">
        <div className="container px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              {t('workHeight.targetAudience.title')}
            </h2>
          </motion.div>

          <div className="flex flex-wrap justify-center gap-4 max-w-4xl mx-auto">
            {targetAudience.map((audience, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
                className="flex items-center gap-2 bg-card px-5 py-3 rounded-full border border-border shadow-sm"
              >
                <Users className="w-4 h-4 text-accent" />
                <span className="text-foreground font-medium">{audience}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Legal Requirements */}
      <section className="section-padding section-alt">
        <div className="container px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              {t('common.legalRequirementsTitle')}
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
            {legalRequirements.map((req, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-primary rounded-2xl p-6 text-primary-foreground"
              >
                <AlertTriangle className="w-8 h-8 text-accent mb-3" />
                <h3 className="font-bold mb-2">{req.title}</h3>
                <p className="text-primary-foreground/80 text-sm">{req.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      {faqs.length > 0 && (
        <section className="section-padding bg-background">
          <div className="container px-4">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
                {t('common.faqTitle')}
              </h2>
            </motion.div>

            <div className="max-w-3xl mx-auto space-y-4">
              {faqs.map((faq, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="card-glass p-6"
                >
                  <h3 className="text-lg font-bold text-foreground mb-2">{faq.question}</h3>
                  <p className="text-muted-foreground">{faq.answer}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="section-padding bg-primary">
        <div className="container px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-3xl mx-auto"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-primary-foreground mb-4">
              {t('workHeight.cta.title')}
            </h2>
            <p className="text-xl text-primary-foreground/80 mb-8">
              {t('workHeight.cta.subtitle')}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact">
                <Button variant="cta" size="lg" className="gap-2 text-lg px-8">
                  <Phone className="w-5 h-5" />
                  {t('common.orderTraining')}
                </Button>
              </Link>
              <Link to="/trainings">
                <Button variant="outline" size="lg" className="gap-2 text-lg px-8 border-primary-foreground/30 text-primary-foreground hover:bg-primary-foreground/10">
                  {t('common.moreTrainings')}
                  <ArrowIcon className="w-5 h-5" />
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </Layout>
  );
}
